import Editor from '@monaco-editor/react';
import CodeIcon from '@mui/icons-material/Code';
import ReplayIcon from '@mui/icons-material/Replay';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Button, Stack, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip from '@mui/material/Tooltip';
import axios from 'axios';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { InterviewContext } from '../../global/InterviewContext';
import { API_URL } from '../../global/constants';
import { VideoAndScreenRecorder } from '../VideoAndScreenRecorder';
import LanguagesDropdown from './LanguagesDropdown';
import LoadingDialog from './LoadingDialog';
import Questions from './Questions';
import ResetCodeDialog from './ResetCodeDialog';
import SubmitCodeDialog from './SubmitCodeDialog';
import TestCompleteDialog from './TestCompleteDialog';
import ThemeDropdown from './ThemeDropdown';
import { languageOptions } from './constants/languageOptions';
import { defineTheme } from './lib/defineTheme';
import { useDispatch, useSelector } from 'react-redux';
import {
	selectInterviewData,
	selectisproctoringcheckdone,
	setIsProctoringCheckDone,
} from '../../features/interview/interview';
import { PROCTORING_TOOL } from '../../global/constants';

const MIN_LEFT_PANEL_WIDTH = 20;
const MAX_LEFT_PANEL_WIDTH = 60;
const DEFAULT_CODING_TEST_DURATION_MINUTES = 60;

const getTimeLeftStorageKey = (interviewKey) => `timeLeft_${interviewKey}`;

const getLanguageOption = (languageValue) => {
	return languageOptions.find((option) => option.value === languageValue) || languageOptions[0];
};

const toClockTime = (totalMinutes) => {
	const safeMinutes =
		Number.isFinite(totalMinutes) && totalMinutes >= 0
			? Math.floor(totalMinutes)
			: DEFAULT_CODING_TEST_DURATION_MINUTES;

	return {
		hours: Math.floor(safeMinutes / 60),
		minutes: safeMinutes % 60,
		seconds: 0,
	};
};

const parseStoredTime = (storedTime) => {
	if (!storedTime) {
		return null;
	}

	try {
		const parsedTime = JSON.parse(storedTime);
		const hours = Number(parsedTime?.hours);
		const minutes = Number(parsedTime?.minutes);
		const seconds = Number(parsedTime?.seconds);

		if (![hours, minutes, seconds].every((value) => Number.isInteger(value) && value >= 0)) {
			return null;
		}

		return { hours, minutes, seconds };
	} catch {
		return null;
	}
};

const CodeEditor = () => {
	const editorRef = useRef(null);
	const interview_data = useSelector(selectInterviewData);
	const Proctoringcheckpassed = useSelector(selectisproctoringcheckdone);
	const dispatch = useDispatch();

	const { fullName, isDemo, isStudentInterview, candidate_interview, proctoring_token } =
		interview_data;
	const { proctoring_tool, _id: interview_key } = candidate_interview;
	const { setInterviewEnded, interviewEnded } = useContext(InterviewContext);
	const timeStorageKey = interview_key ? getTimeLeftStorageKey(interview_key) : null;

	const navigate = useNavigate();
	const outputRef = useRef(null);
	const editorLayoutRef = useRef(null);
	const [theme, setTheme] = useState('cobalt');
	const [language, setLanguage] = useState(languageOptions[0]);
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	const [leftPanelWidth, setLeftPanelWidth] = useState(30);
	const [isDraggingDivider, setIsDraggingDivider] = useState(false);
	const [time, setTime] = useState(toClockTime(DEFAULT_CODING_TEST_DURATION_MINUTES));
	const [resetCodeDialogOpen, setResetCodeDialogOpen] = useState(false);
	const [submitCodeDialogOpen, setSubmitCodeDialogOpen] = useState(false);
	const [testCompleteDialogOpen, setTestCompleteDialogOpen] = useState(false);
	const [questions, setQuestions] = useState([]);
	const [userData, setUserData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saveLoading, setSaveLoading] = useState(false);

	const [submissionId, setSubmissionId] = useState(null);
	const [errorInCode, setErrorInCode] = useState(null);
	const [codeExecutionStatus, setCodeExecutionStatus] = useState(null);
	const [codeExecutionTime, setCodeExecutionTime] = useState(null);
	const [codeExecutionMemory, setCodeExecutionMemory] = useState(null);
	const [testCaseResults, setTestCaseResults] = useState(null);

	// tab switch check
	const [alertDialogOpen, setAlertDialogOpen] = useState(false);
	// const [tabSwitches, setTabSwitches] = useState(0);
	let tabSwitches = 0;
	let timer;
	const [isInterviewRequired, setIsInterviewRequired] = useState(false);
	useEffect(() => {
		async function fetchinterview() {
			const data = await axios.get(`${API_URL}/labs/interview/${interview_key}`);
			const interviewdetails = data.data;
			console.log(interviewdetails, 'interviewdetails');
			const is_interview_required =
				interviewdetails?.candidate_interview?.is_interview_required;
			setIsInterviewRequired(is_interview_required);
		}
		fetchinterview();
	}, []);
	const handleEditorMount = (editor, monaco) => {
		editorRef.current = editor;
		editorRef.current.onKeyDown((event) => {
			const { keyCode, ctrlKey, metaKey } = event;
			if ((keyCode === 33 || keyCode === 52) && (metaKey || ctrlKey)) {
				event.preventDefault();
			}
		});
	};

	const endUserInterview = async (endInterviewReason, tab_switches) => {
		await axios.post(`${API_URL}/labs/interview/end`, {
			interview_key: interview_key,
			end_interview_reason: endInterviewReason,
			tab_switches,
		});
		setInterviewEnded(true);
		// setUploadVideo(true);
	};

	useEffect(() => {
		if (alertDialogOpen) {
			timer = setInterval(() => {
				// endUserInterview('violation', tabSwitches);
			}, [10000]);
		} else {
			clearInterval(timer);
		}
		return () => clearInterval(timer);
	}, [alertDialogOpen]);

	useEffect(() => {
		if (proctoring_token && proctoring_tool === PROCTORING_TOOL) {
			window.remoteProctoring
				.remoteProctorInitV2({ authToken: proctoring_token })
				.then(function () {
					console.log('Initialization is success');
					//TODO: start test player here
					dispatch(setIsProctoringCheckDone(true));
				})
				.catch(function (err) {
					console.log('Initialization failed', err);
					alert('Initialization failed');
				});
		}

		console.log('loaded');
	}, [proctoring_token]);
	console.log('proctoring_token', proctoring_token);

	const exitRefresh = useCallback((e) => {
		e.preventDefault();
		e.returnValue = '';
	});
	const checkTabSwitches = useCallback(() => {
		if (!interviewEnded && proctoring_tool !== PROCTORING_TOOL) {
			setAlertDialogOpen(true);
			tabSwitches++;
			if (tabSwitches > 3) {
				// setAlertDialogOpen(false);
				// console.log("You have switched tabs more than 3 times.");
				// alert('You violated the terms of the interview. The interview has been ended.');
				// endUserInterview('violaton', tabSwitches);
				setAlertDialogOpen(false);
			}
		}
	});

	useEffect(() => {
		if (!interviewEnded) {
			window.addEventListener('blur', checkTabSwitches);
		} else {
			window.removeEventListener('blur', checkTabSwitches);
		}
		return () => {
			window.removeEventListener('beforeunload', exitRefresh);
			window.removeEventListener('blur', checkTabSwitches);
		};
	}, [interviewEnded]);

	useEffect(() => {
		if (!isDraggingDivider) {
			return;
		}

		const handleMouseMove = (event) => {
			if (!editorLayoutRef.current) {
				return;
			}

			const { left, width } = editorLayoutRef.current.getBoundingClientRect();
			if (!width) {
				return;
			}

			const nextWidth = ((event.clientX - left) / width) * 100;
			const clampedWidth = Math.min(
				MAX_LEFT_PANEL_WIDTH,
				Math.max(MIN_LEFT_PANEL_WIDTH, nextWidth)
			);
			setLeftPanelWidth(clampedWidth);
		};

		const handleMouseUp = () => {
			setIsDraggingDivider(false);
		};

		const previousCursor = document.body.style.cursor;
		const previousUserSelect = document.body.style.userSelect;
		document.body.style.cursor = 'col-resize';
		document.body.style.userSelect = 'none';

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);

		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
			document.body.style.cursor = previousCursor;
			document.body.style.userSelect = previousUserSelect;
		};
	}, [isDraggingDivider]);

	const resetValues = () => {
		setErrorInCode(null);
		setCodeExecutionStatus(null);
		setCodeExecutionTime(null);
		setCodeExecutionMemory(null);
		setTestCaseResults(null);
		setSubmissionId(null);
	};

	const handleCompile = async () => {
		saveCode(false);
		resetValues();
		setLoading(true);
		const { data } = await axios.post(`${API_URL}/labs/coding-test/compile-code`, {
			interview_key,
			question_id: questions[currentQuestionIndex]._id,
			code: userData[currentQuestionIndex].code[language.value],
			language: language.value,
		});
		if (data.success) {
			showSuccessToast(data.message);
			getSubmissionStatus(data.submission_id);
		} else {
			showErrorToast(data.message);
		}
		setLoading(false);
	};

	const getSubmissionStatus = async (submission_id) => {
		setLoading(true);
		const { data } = await axios.post(`${API_URL}/labs/coding-test/get-submission-status`, {
			submission_id,
		});
		const results = data.results;
		for (const result of results) {
			if (result.status_id === 1 || result.status_id === 2) {
				setTimeout(() => {
					getSubmissionStatus(submission_id);
				}, 2000);
				return;
			}
			if (result.error) {
				setLoading(false);
				setCodeExecutionStatus(result.status_description);
				setCodeExecutionTime(result.time);
				setCodeExecutionMemory(result.memory);
				setErrorInCode(atob(result.error));
				return;
			}
		}
		setTestCaseResults(results);
		setSubmissionId(submission_id);
		if (data.success) {
			// showSuccessToast(data.message);
			outputRef.current.scrollIntoView();
		} else {
			showErrorToast(data.message);
		}
		setLoading(false);
	};

	function handleThemeChange(th) {
		const theme = th;

		if (['light', 'vs-dark'].includes(theme.value)) {
			setTheme(theme);
		} else {
			defineTheme(theme.value).then((_) => setTheme(theme));
		}
	}
	useEffect(() => {
		defineTheme('oceanic-next').then((_) =>
			setTheme({ value: 'oceanic-next', label: 'Oceanic Next' })
		);
	}, []);

	const showSuccessToast = (msg) => {
		toast.success(msg || `Compiled Successfully!`, {
			position: 'top-right',
			autoClose: 1000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
		});
	};

	const showErrorToast = (msg, timer) => {
		toast.error(msg || `Something went wrong! Please try again.`, {
			position: 'top-right',
			autoClose: timer ? timer : 1000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
		});
	};

	const goToNotSubmittedQuestion = () => {
		for (let ud of userData) {
			if (!ud.submitted) {
				setCurrentQuestionIndex(userData.indexOf(ud));
				return;
			}
		}
		setTestCompleteDialogOpen(true);
	};

	const resetCode = () => {
		resetValues();
		let newUserData = [...userData];
		newUserData[currentQuestionIndex].code[language.value] =
			questions[currentQuestionIndex].code_snippet[language.value];
		setUserData(newUserData);
	};

	const getData = async () => {
		setLoading(true);
		const { data } = await axios.post(`${API_URL}/labs/coding-test/get-questions`, {
			interview_key,
		});
		if (data.success) {
			setQuestions(data.questions);
			setUserData(data.user_data);
			const parsedTimeLimit = Number(data.time_limit);
			const safeTimeLimit =
				Number.isFinite(parsedTimeLimit) && parsedTimeLimit > 0
					? Math.floor(parsedTimeLimit)
					: DEFAULT_CODING_TEST_DURATION_MINUTES;
			const defaultInterviewTime = toClockTime(safeTimeLimit);
			const storedInterviewTime =
				timeStorageKey && parseStoredTime(localStorage.getItem(timeStorageKey));
			const resolvedInterviewTime = storedInterviewTime || defaultInterviewTime;

			setTime(resolvedInterviewTime);

			if (timeStorageKey && !storedInterviewTime) {
				localStorage.setItem(timeStorageKey, JSON.stringify(resolvedInterviewTime));
			}

			for (let ud of data.user_data) {
				if (!ud.submitted) {
					setLanguage(
						getLanguageOption(data.questions?.[data.user_data.indexOf(ud)]?.language[0])
					);
					setCurrentQuestionIndex(data.user_data.indexOf(ud));
					setLoading(false);
					return;
				}
			}
			setLanguage(getLanguageOption(data.questions?.[0]?.language[0]));
		}
		setLoading(false);
		setTestCompleteDialogOpen(true);
	};

	const saveCode = async (showToast) => {
		setSaveLoading(true);
		const { data } = await axios.post(`${API_URL}/labs/coding-test/save-code`, {
			interview_key,
			codes: userData,
		});
		if (data.success) {
			showToast && showSuccessToast(data.message);
		} else {
			showErrorToast(data.message);
		}
		setSaveLoading(false);
	};

	const submitCode = async () => {
		setLoading(true);
		const { data } = await axios.post(`${API_URL}/labs/coding-test/submit-code`, {
			submission_id: submissionId,
		});
		if (data.success) {
			showSuccessToast(data.message);
			let newUserData = [...userData];
			newUserData[currentQuestionIndex].submitted = true;
			setUserData(newUserData);
			goToNotSubmittedQuestion();
			resetValues();
		} else {
			showErrorToast(data.message);
		}
		setLoading(false);
	};

	const handleGoToNextSection = async () => {
		setLoading(true);
		const { data } = await axios.post(`${API_URL}/labs/coding-test/end`, {
			interview_key,
		});
		if (data.success) {
			showSuccessToast(data.message);
			if (!isInterviewRequired) {
				navigate(`/end/coding/${fullName}/${interview_key}`, {
					state: {
						isDemo: false,
						isStudentInterview,
					},
				});
			} else {
				navigate(`/interview`);
			}
		} else {
			showErrorToast(data.message);
		}
		setLoading(false);
	};

	useEffect(() => {
		if (interview_key) {
			getData();
		}
	}, [interview_key]);

	useEffect(() => {
		if (interview_key && userData.length > 0) {
			saveCode();
		}
	}, [currentQuestionIndex, language, theme]);

	useEffect(() => {
		if (!timeStorageKey || testCompleteDialogOpen) {
			return;
		}

		const timer = setInterval(() => {
			const currentTime = parseStoredTime(localStorage.getItem(timeStorageKey));

			if (!currentTime) {
				return;
			}

			if (currentTime.hours === 0 && currentTime.minutes === 0 && currentTime.seconds === 0) {
				setTime(currentTime);
				clearInterval(timer);
				setTestCompleteDialogOpen(true);
				return;
			}

			let newSeconds = currentTime.seconds - 1;
			let newMinutes = currentTime.minutes;
			let newHours = currentTime.hours;

			if (newSeconds < 0) {
				newMinutes -= 1;
				newSeconds = 59;
			}
			if (newMinutes < 0) {
				newHours -= 1;
				newMinutes = 59;
			}

			const timeObj = {
				hours: Math.max(newHours, 0),
				minutes: Math.max(newMinutes, 0),
				seconds: Math.max(newSeconds, 0),
			};

			const isTimeOver =
				timeObj.hours === 0 && timeObj.minutes === 0 && timeObj.seconds === 0;

			setTime(timeObj);
			localStorage.setItem(timeStorageKey, JSON.stringify(timeObj));

			if (isTimeOver) {
				clearInterval(timer);
				setTestCompleteDialogOpen(true);
			}
		}, 1000);

		return () => clearInterval(timer);
	}, [timeStorageKey, testCompleteDialogOpen]);
	useEffect(() => {
		const endTest = async () => {
			await axios.post(`${API_URL}/labs/coding-test/end`, {
				interview_key,
			});
			if (!isInterviewRequired) {
				navigate(`/end/coding/${fullName}/${interview_key}`);
			}
		};
		if (testCompleteDialogOpen && proctoring_tool === PROCTORING_TOOL) {
			window.remoteProctoring
				.stop()
				.then(function () {
					endTest();
					console.log('Remote Proctoring session ended successfully');
				})
				.catch(function (err) {
					console.log('Remote Proctoring session end FAILED', err);
				});
		}
		if (testCompleteDialogOpen) {
			endTest();
		}
	}, [testCompleteDialogOpen]);
	if (proctoring_token && !Proctoringcheckpassed) {
		return <></>;
	}

	return (
		<Stack>
			<Dialog open={alertDialogOpen && !proctoring_token}>
				<DialogTitle display="flex" alignItems={'center'}>
					<WarningAmberIcon color="red" />
					<b>Warning</b>
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						We noticed that you moved out of the interview.{' '}
						<b>The hiring team is tracking</b> this activity. We recommend staying on
						this page until you complete the interview.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						variant="contained"
						style={{
							textTransform: 'none',
							color: '#fff',
							backgroundColor: 'green',
						}}
						onClick={() => {
							setAlertDialogOpen(false);
						}}
					>
						Agree & Continue
					</Button>
				</DialogActions>
			</Dialog>
			<ToastContainer />
			<LoadingDialog open={loading} />
			{isInterviewRequired && (
				<TestCompleteDialog
					open={testCompleteDialogOpen}
					isInterviewRequired={isInterviewRequired}
					handleGoToNextSection={handleGoToNextSection}
				/>
			)}

			{questions.length > 0 && (
				<Stack direction={'row'} ref={editorLayoutRef} sx={{ width: '100%' }}>
					<Stack
						width={`${leftPanelWidth}%`}
						minWidth={'260px'}
						bgcolor={'#fff'}
						spacing={2}
						p={2}
						pl={0}
						pr={1}
					>
						<Typography fontWeight={'bold'} justifySelf={'end'} pl={2}>
							Time Remaining: {String(time.hours).padStart(2, '0')}:
							{String(time.minutes).padStart(2, '0')}:
							{String(time.seconds).padStart(2, '0')}
						</Typography>
						<Questions
							questions={questions}
							currentQuestionIndex={currentQuestionIndex}
							setCurrentQuestionIndex={setCurrentQuestionIndex}
							resetValues={resetValues}
							userData={userData}
						/>
					</Stack>
					<Stack
						onMouseDown={(event) => {
							event.preventDefault();
							setIsDraggingDivider(true);
						}}
						sx={{
							width: '10px',
							cursor: 'col-resize',
							flexShrink: 0,
							position: 'relative',
							'&::before': {
								content: '""',
								position: 'absolute',
								top: 0,
								bottom: 0,
								left: '50%',
								transform: 'translateX(-50%)',
								width: '2px',
								bgcolor: 'divider',
								borderRadius: '2px',
							},
							'&:hover::before': {
								bgcolor: '#224c84',
							},
						}}
					/>
					<Stack flex={1} minWidth={0} spacing={2} p={2} pl={1}>
						<Stack spacing={2} direction={'row'} alignItems={'center'} w={'100%'}>
							<Typography fontWeight={'bold'}>Select Language</Typography>

							<LanguagesDropdown
								value={language}
								onSelectChange={(sl) => {
									setLanguage(sl);
									resetValues();
								}}
							/>
							<Typography fontWeight={'bold'}>Select Theme</Typography>
							<ThemeDropdown handleThemeChange={handleThemeChange} theme={theme} />
							<ResetCodeDialog
								open={resetCodeDialogOpen}
								setOpen={setResetCodeDialogOpen}
								handleResetCode={resetCode}
							/>
							<Tooltip
								title="Reset to boilerplate code"
								placement="bottom"
								arrow
								followCursor
							>
								<Button
									startIcon={<ReplayIcon />}
									variant={'contained'}
									onClick={() => setResetCodeDialogOpen(true)}
								>
									Reset Code
								</Button>
							</Tooltip>
							<Button
								startIcon={<SaveIcon />}
								variant={'contained'}
								onClick={() => saveCode(true)}
								disabled={saveLoading}
							>
								Save Code
							</Button>
						</Stack>
						<Editor
							height="80vh"
							width="100%"
							onMount={handleEditorMount}
							language={language.value}
							value={userData[currentQuestionIndex].code[language.value] ?? ''}
							theme={theme.value}
							defaultValue={userData[currentQuestionIndex].code[language.value] ?? ''}
							onChange={(value) => {
								let newUserData = [...userData];
								newUserData[currentQuestionIndex].code[language.value] = value;
								setUserData(newUserData);
							}}
							options={{
								contextmenu: false,
							}}
						/>
						<Stack
							direction={'row'}
							spacing={2}
							justifyContent={'end'}
							alignItems={'end'}
						>
							<Tooltip
								title="Run code to test against sample test cases"
								placement="left"
								arrow
								followCursor
							>
								<Button
									variant="outlined"
									onClick={handleCompile}
									startIcon={<CodeIcon />}
								>
									Run Code
								</Button>
							</Tooltip>
							<SubmitCodeDialog
								open={submitCodeDialogOpen}
								setOpen={setSubmitCodeDialogOpen}
								handleSubmitCode={submitCode}
							/>
							<Tooltip
								title={
									!submissionId
										? 'Please run the code first.'
										: 'Submit code for evaluation'
								}
								placement="left"
								arrow
								followCursor
							>
								{submissionId ? (
									<Button
										variant="contained"
										onClick={() => setSubmitCodeDialogOpen(true)}
										disabled={!submissionId}
									>
										Submit Code
									</Button>
								) : (
									<span>
										<Button
											variant="contained"
											onClick={() => setSubmitCodeDialogOpen(true)}
											disabled={!submissionId}
										>
											Submit Code
										</Button>
									</span>
								)}
							</Tooltip>
						</Stack>
						<div ref={outputRef}>
							<Stack spacing={2}>
								{(codeExecutionStatus ||
									(testCaseResults && testCaseResults.length > 0)) && (
									<Typography fontWeight={'bold'} fontSize={'1.3rem'}>
										Output
									</Typography>
								)}
								{!testCaseResults && (
									<Stack spacing={1}>
										{codeExecutionStatus && (
											<Typography fontWeight={'bold'}>
												Status: {codeExecutionStatus}
											</Typography>
										)}
										{codeExecutionTime && (
											<Typography fontWeight={'bold'}>
												Time: {codeExecutionTime}
											</Typography>
										)}
										{codeExecutionMemory && (
											<Typography fontWeight={'bold'}>
												Memory: {codeExecutionMemory}
											</Typography>
										)}
										{errorInCode && (
											<Typography
												fontWeight={'bold'}
												color={'red'}
												fontSize={'0.7rem'}
											>
												{errorInCode}
											</Typography>
										)}
									</Stack>
								)}
								{testCaseResults &&
									testCaseResults.map((result, index) => (
										<Stack
											spacing={1}
											border={`2px solid ${
												result.status_id === 3 ? '#027148' : '#e53935'
											}`}
											borderRadius={'10px'}
											direction="row"
											p={2}
											bgcolor={
												result.status_id === 3
													? 'rgb(2, 113, 72, 0.5)'
													: 'rgb(229, 57, 53, 0.5)'
											}
										>
											<Typography fontWeight={'bold'}>
												Test Case: {index + 1}
											</Typography>
											<Typography fontWeight={'bold'}>
												Status: {result.status_description}
											</Typography>
											<Typography fontWeight={'bold'}>
												Time: {result.time}
											</Typography>
											<Typography fontWeight={'bold'}>
												Memory: {result.memory}
											</Typography>
										</Stack>
									))}
							</Stack>
						</div>
						{/* {!proctoring_token && ( */}
						<VideoAndScreenRecorder
							interviewKey={interview_key}
							isStudentInterview={isStudentInterview}
							fullName={fullName}
						/>
						{/* )} */}
					</Stack>
				</Stack>
			)}
		</Stack>
	);
};
export default CodeEditor;
